import { useEffect } from 'react';
import { playSound } from './sounds';

/**
 * Puente entre los toasts de sonner y el sistema de sonidos.
 *
 * Monta un MutationObserver sobre el contenedor `[data-sonner-toaster]` y
 * dispara el sonido adecuado según el `data-type` del toast que aparece.
 * Usa un `WeakSet` para no repetir sonido si el toast se re-renderiza.
 */
export function SoundBridge() {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') return;

    const seen = new WeakSet<Element>();

    function attach() {
      const container = document.querySelector('[data-sonner-toaster]');
      if (!container) return null;

      const handleNode = (node: Element) => {
        if (!(node instanceof HTMLElement)) return;
        if (!node.hasAttribute('data-sonner-toast')) return;
        if (seen.has(node)) return;
        seen.add(node);

        const type = node.getAttribute('data-type');
        if (type === 'success') playSound('success');
        else if (type === 'error' || type === 'warning') playSound('alert');
        else playSound('notify');
      };

      const obs = new MutationObserver((mutations) => {
        for (const m of mutations) {
          m.addedNodes.forEach((n) => {
            if (n instanceof HTMLElement) {
              handleNode(n);
              n.querySelectorAll?.('[data-sonner-toast]').forEach(handleNode);
            }
          });
        }
      });
      obs.observe(container, { childList: true, subtree: true });
      return obs;
    }

    // El Toaster puede montarse después que SoundBridge; reintentamos.
    let obs = attach();
    const retry = setInterval(() => {
      if (obs) return;
      obs = attach();
    }, 300);
    setTimeout(() => clearInterval(retry), 3000);

    return () => {
      clearInterval(retry);
      obs?.disconnect();
    };
  }, []);

  return null;
}
